import React, { useState, useEffect } from 'react';
import {
  Tab,
  Nav,
  Row,
  Col,
  Badge,
  Table,
  Button,
  Alert,
  Form,
  InputGroup
} from 'react-bootstrap';
// import { useHVAHealthCare } from './HVAHealthCareContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHospital,
  faUserMd,
  faMicroscope,
  faCalendarCheck,
  faCheck,
  faTimes,
  faUserNurse,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import {
  healthCareService,
  Location,
  Examination,
  Doctor,
  Specialty,
  TimeSlot
} from './health-service';

/**
 * Dynamic InfoPanel that shows locations, examinations, doctors, specialties and available slots
 */
const HVAHealthCareInfoPanel: React.FC = () => {
  // State for the active tab
  const [activeTab, setActiveTab] = useState('locations');

  // State for data from service
  const [locations, setLocations] = useState<Location[]>([]);
  const [examinations, setExaminations] = useState<Examination[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);

  // State for selections
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [selectedExamination, setSelectedExamination] = useState<string | null>(
    null
  );
  const [selectedDoctor, setSelectedDoctor] = useState<string | null>(null);
  const [selectedSpecialty, setSelectedSpecialty] = useState<string | null>(
    null
  );

  // State for filters
  const [locationFilter, setLocationFilter] = useState('');
  const [examinationFilter, setExaminationFilter] = useState('');
  const [doctorFilter, setDoctorFilter] = useState('');
  const [specialtyFilter, setSpecialtyFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');

  // State for loading
  const [isLoading, setIsLoading] = useState(true);

  // Load data from service
  useEffect(() => {
    try {
      setLocations(healthCareService.getLocations());
      setExaminations(healthCareService.getExaminations());
      setDoctors(healthCareService.getDoctors());
      setSpecialties(healthCareService.getSpecialties());
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setIsLoading(false);
    }
  }, []);

  // Load available slots based on current selection
  useEffect(() => {
    // Reset available slots when switching tabs
    setAvailableSlots([]);

    // Reset selections when changing tabs
    if (activeTab === 'locations') {
      setSelectedDoctor(null);
      setSelectedExamination(null);
      setSelectedSpecialty(null);
    } else if (activeTab === 'examinations') {
      setSelectedDoctor(null);
      setSelectedLocation(null);
      setSelectedSpecialty(null);
    } else if (activeTab === 'doctors') {
      setSelectedExamination(null);
      setSelectedLocation(null);
      setSelectedSpecialty(null);
    } else if (activeTab === 'specialties') {
      setSelectedDoctor(null);
      setSelectedExamination(null);
      setSelectedLocation(null);
    }
  }, [activeTab]);

  // Load slots based on selections - with added error handling
  useEffect(() => {
    // Helper function to safely get slots with error handling
    const safeGetSlots = (params: {
      locationId: string;
      serviceType: 'examination' | 'doctor';
      serviceId: string;
    }) => {
      try {
        return healthCareService.findAvailableSlots(params);
      } catch (error) {
        console.error('Error loading slots:', error, params);
        return [];
      }
    };

    if (activeTab === 'locations' && selectedLocation) {
      // If a location is selected, get all slots for that location
      try {
        // Get examination slots
        const examSlots = examinations.flatMap(exam => {
          if (!exam || !exam.id) return [];
          return safeGetSlots({
            locationId: selectedLocation,
            serviceType: 'examination',
            serviceId: exam.id
          });
        });

        // Get doctor slots
        const doctorSlots = doctors.flatMap(doctor => {
          if (!doctor || !doctor.id || !doctor.locationIds) return [];
          if (doctor.locationIds.includes(selectedLocation)) {
            return safeGetSlots({
              locationId: selectedLocation,
              serviceType: 'doctor',
              serviceId: doctor.id
            });
          }
          return [];
        });

        // Combined slots
        const combinedSlots = [...examSlots, ...doctorSlots].filter(
          slot => slot && slot.date && slot.startTime
        );
        setAvailableSlots(combinedSlots);
      } catch (error) {
        console.error('Error loading slots for location:', error);
        setAvailableSlots([]);
      }
    } else if (activeTab === 'examinations' && selectedExamination) {
      // If an examination is selected, get all slots for that examination
      try {
        const slots = locations.flatMap(location => {
          if (!location || !location.id) return [];
          return safeGetSlots({
            locationId: location.id,
            serviceType: 'examination',
            serviceId: selectedExamination
          });
        });

        setAvailableSlots(
          slots.filter(slot => slot && slot.date && slot.startTime)
        );
      } catch (error) {
        console.error('Error loading slots for examination:', error);
        setAvailableSlots([]);
      }
    } else if (activeTab === 'doctors' && selectedDoctor) {
      // If a doctor is selected, get all slots for that doctor
      try {
        const doctor = doctors.find(d => d.id === selectedDoctor);
        if (doctor && doctor.locationIds) {
          const slots = doctor.locationIds.flatMap(locationId => {
            if (!locationId) return [];
            return safeGetSlots({
              locationId,
              serviceType: 'doctor',
              serviceId: selectedDoctor
            });
          });

          setAvailableSlots(
            slots.filter(slot => slot && slot.date && slot.startTime)
          );
        } else {
          setAvailableSlots([]);
        }
      } catch (error) {
        console.error('Error loading slots for doctor:', error);
        setAvailableSlots([]);
      }
    } else if (activeTab === 'specialties' && selectedSpecialty) {
      // If a specialty is selected, get all slots for all doctors of that specialty
      try {
        const specialtyDoctors = doctors.filter(
          d => d && d.specialtyId === selectedSpecialty
        );

        const slots = specialtyDoctors.flatMap(doctor => {
          if (!doctor || !doctor.id || !doctor.locationIds) return [];
          return doctor.locationIds.flatMap(locationId => {
            if (!locationId) return [];
            return safeGetSlots({
              locationId,
              serviceType: 'doctor',
              serviceId: doctor.id
            });
          });
        });

        setAvailableSlots(
          slots.filter(slot => slot && slot.date && slot.startTime)
        );
      } catch (error) {
        console.error('Error loading slots for specialty:', error);
        setAvailableSlots([]);
      }
    }
  }, [
    selectedLocation,
    selectedExamination,
    selectedDoctor,
    selectedSpecialty,
    activeTab,
    examinations,
    doctors,
    locations
  ]);

  // Filter slots by date if date filter is set - with added validation
  const filteredSlots = availableSlots.filter(slot => {
    // First ensure the slot exists and has a date
    if (!slot || !slot.date) return false;

    // If date filter is active, only include slots matching the filter
    if (dateFilter && dateFilter.trim() !== '') {
      return slot.date === dateFilter;
    }

    // Otherwise include all valid slots
    return true;
  });

  // Group slots by date - ensure we handle null/undefined values
  const slotsByDate = filteredSlots.reduce(
    (groups: Record<string, TimeSlot[]>, slot) => {
      // Only process slots with valid date
      if (slot && slot.date) {
        if (!groups[slot.date]) {
          groups[slot.date] = [];
        }
        groups[slot.date].push(slot);
      }
      return groups;
    },
    {}
  );

  // Format slot date for display
  const formatDate = (dateStr: string) => {
    if (!dateStr) return 'Unknown Date';

    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      console.error('Error formatting date:', e);
      return 'Invalid Date';
    }
  };

  // Get location name by ID - with safety checks
  const getLocationName = (locationId: string) => {
    if (!locationId) return 'Unknown Location';

    try {
      const location = locations.find(loc => loc && loc.id === locationId);
      return location && location.name ? location.name : 'Unknown Location';
    } catch (error) {
      console.error('Error getting location name:', error);
      return 'Error: Location not found';
    }
  };

  // Get doctor name by ID - with safety checks
  const getDoctorName = (doctorId: string) => {
    if (!doctorId) return 'Unknown Doctor';

    try {
      const doctor = doctors.find(doc => doc && doc.id === doctorId);
      if (!doctor || !doctor.firstName || !doctor.lastName)
        return 'Unknown Doctor';

      const firstName = doctor.firstName.es || '';
      const lastName = doctor.lastName.es || '';
      return `${firstName} ${lastName}`.trim() || 'Unknown Doctor';
    } catch (error) {
      console.error('Error getting doctor name:', error);
      return 'Error: Doctor not found';
    }
  };

  // Get examination name by ID - with safety checks
  const getExaminationName = (examinationId: string) => {
    if (!examinationId) return 'Unknown Examination';

    try {
      const examination = examinations.find(
        exam => exam && exam.id === examinationId
      );
      if (!examination || !examination.name) return 'Unknown Examination';

      return examination.name.es || 'Unknown Examination';
    } catch (error) {
      console.error('Error getting examination name:', error);
      return 'Error: Examination not found';
    }
  };

  // Get specialty name by ID - with safety checks
  const getSpecialtyName = (specialtyId: string) => {
    if (!specialtyId) return 'Unknown Specialty';

    try {
      const specialty = specialties.find(
        spec => spec && spec.id === specialtyId
      );
      if (!specialty || !specialty.name) return 'Unknown Specialty';

      return specialty.name.es || 'Unknown Specialty';
    } catch (error) {
      console.error('Error getting specialty name:', error);
      return 'Error: Specialty not found';
    }
  };

  // Safe string lowercase helper
  const safeIncludes = (text: string | undefined, search: string): boolean => {
    if (!text) return false;
    try {
      return text.toLowerCase().includes(search.toLowerCase());
    } catch (e) {
      return false;
    }
  };

  // Filtered locations based on search - with safety checks
  const filteredLocations = locations.filter(location => {
    if (!location) return false;
    const searchTerm = locationFilter.toLowerCase();
    return (
      safeIncludes(location.name, searchTerm) ||
      safeIncludes(location.area, searchTerm)
    );
  });

  // Filtered examinations based on search - with safety checks
  const filteredExaminations = examinations.filter(examination => {
    if (!examination || !examination.name) return false;
    const searchTerm = examinationFilter.toLowerCase();
    return (
      safeIncludes(examination.name.es, searchTerm) ||
      safeIncludes(examination.name.en, searchTerm) ||
      safeIncludes(examination.code, searchTerm)
    );
  });

  // Filtered doctors based on search - with safety checks
  const filteredDoctors = doctors.filter(doctor => {
    if (!doctor || !doctor.firstName || !doctor.lastName) return false;
    const searchTerm = doctorFilter.toLowerCase();

    const fullNameEs = `${doctor.firstName.es || ''} ${
      doctor.lastName.es || ''
    }`.trim();
    const fullNameEn = `${doctor.firstName.en || ''} ${
      doctor.lastName.en || ''
    }`.trim();
    const specialty = doctor.specialtyId
      ? getSpecialtyName(doctor.specialtyId)
      : '';

    return (
      safeIncludes(fullNameEs, searchTerm) ||
      safeIncludes(fullNameEn, searchTerm) ||
      safeIncludes(specialty, searchTerm)
    );
  });

  // Filtered specialties based on search - with safety checks
  const filteredSpecialties = specialties.filter(specialty => {
    if (!specialty || !specialty.name) return false;
    const searchTerm = specialtyFilter.toLowerCase();
    return (
      safeIncludes(specialty.name.es, searchTerm) ||
      safeIncludes(specialty.name.en, searchTerm)
    );
  });

  // Render loading state
  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center fs-9"
        style={{ height: '250px' }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-100 fs-9">
      <h5 className="mb-3">Health Care System Information</h5>

      <Tab.Container activeKey={activeTab} onSelect={k => k && setActiveTab(k)}>
        <Nav variant="tabs" className="mb-1">
          <Nav.Item>
            <Nav.Link eventKey="locations">
              <FontAwesomeIcon icon={faHospital} className="me-1" />
              Locations{' '}
              <Badge className="bg-body-quaternary">{locations.length}</Badge>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="examinations">
              <FontAwesomeIcon icon={faMicroscope} className="me-1" />
              Examinations{' '}
              <Badge className="bg-body-quaternary">
                {examinations.length}
              </Badge>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="doctors">
              <FontAwesomeIcon icon={faUserMd} className="me-1" />
              Doctors{' '}
              <Badge className="bg-body-quaternary">{doctors.length}</Badge>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="specialties">
              <FontAwesomeIcon icon={faUserNurse} className="me-1" />
              Specialties{' '}
              <Badge className="bg-body-quaternary">{specialties.length}</Badge>
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content className="pt-2 mb-4">
          {/* LOCATIONS TAB */}
          <Tab.Pane eventKey="locations">
            <div className="mb-1">
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search locations..."
                  value={locationFilter}
                  onChange={e => setLocationFilter(e.target.value)}
                />
                {locationFilter && (
                  <Button
                    variant="secondary"
                    className="bg-body-quaternary"
                    onClick={() => setLocationFilter('')}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                )}
              </InputGroup>
            </div>

            <div
              style={{ height: '250px', overflowY: 'auto' }}
              className="rounded border bg-body-emphasis mb-1 p-2 scrollbar"
            >
              {filteredLocations.map(location => (
                <div
                  key={location.id}
                  className="rounded-3 border bg-body mb-1 p-2"
                  onClick={() =>
                    setSelectedLocation(
                      selectedLocation === location.id ? null : location.id
                    )
                  }
                  style={{ cursor: 'pointer' }}
                >
                  <Row>
                    <Col xs={2} className="text-center">
                      <FontAwesomeIcon
                        icon={faHospital}
                        size="xl"
                        className={
                          selectedLocation === location.id
                            ? 'text-primary'
                            : 'text-body-quaternary'
                        }
                      />
                      <Badge className="bg-body-quaternary">
                        {location.type === 'hospital'
                          ? 'Hospital'
                          : 'Med. Center'}
                      </Badge>
                    </Col>
                    <Col xs={10}>
                      <h6 className="mb-0">{location.name}</h6>
                      <p className="mb-0 lh-sm fs-10">
                        {location.address}
                        <br />
                        Area: {location.area}
                      </p>

                      {selectedLocation === location.id && (
                        <div className="mt-0">
                          <Badge bg="success">Selected</Badge>
                        </div>
                      )}
                    </Col>
                  </Row>
                </div>
              ))}
            </div>
          </Tab.Pane>

          {/* EXAMINATIONS TAB */}
          <Tab.Pane eventKey="examinations">
            <div className="mb-1">
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search examinations..."
                  value={examinationFilter}
                  onChange={e => setExaminationFilter(e.target.value)}
                />
                {examinationFilter && (
                  <Button
                    variant="secondary"
                    className="bg-body-quaternary"
                    onClick={() => setExaminationFilter('')}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                )}
              </InputGroup>
            </div>

            <div
              style={{ height: '250px', overflowY: 'auto' }}
              className="rounded border bg-body-emphasis mb-1 p-2 scrollbar"
            >
              {filteredExaminations.map(exam => (
                <div
                  key={exam.id}
                  className="rounded-3 border bg-body mb-1 p-2"
                  onClick={() =>
                    setSelectedExamination(
                      selectedExamination === exam.id ? null : exam.id
                    )
                  }
                  style={{ cursor: 'pointer' }}
                >
                  <Row>
                    <Col xs={2} className="text-center">
                      <FontAwesomeIcon
                        icon={faMicroscope}
                        size="xl"
                        className={
                          selectedExamination === exam.id
                            ? 'text-primary'
                            : 'text-body-quaternary'
                        }
                      />
                    </Col>
                    <Col xs={10}>
                      <h6 className="mb-0">{exam.name.es}</h6>
                      <p className="mb-0 text-muted fs-10">{exam.name.en}</p>
                      <div>
                        <Badge className="me-2 bg-body-quaternary">
                          Code: {exam.code}
                        </Badge>
                        <Badge className="bg-body-quaternary">
                          Duration: {exam.duration} min
                        </Badge>
                      </div>

                      {selectedExamination === exam.id && (
                        <div className="mt-0">
                          <Badge bg="success">Selected</Badge>
                        </div>
                      )}
                    </Col>
                  </Row>
                </div>
              ))}
            </div>
          </Tab.Pane>

          {/* DOCTORS TAB */}
          <Tab.Pane eventKey="doctors">
            <div className="mb-1">
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search doctors..."
                  value={doctorFilter}
                  onChange={e => setDoctorFilter(e.target.value)}
                />
                {doctorFilter && (
                  <Button
                    variant="secondary"
                    className="bg-body-quaternary"
                    onClick={() => setDoctorFilter('')}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                )}
              </InputGroup>
            </div>

            <div
              style={{ height: '250px', overflowY: 'auto' }}
              className="rounded border bg-body-emphasis mb-1 p-2 scrollbar"
            >
              {filteredDoctors.map(doctor => (
                <div
                  key={doctor.id}
                  className="rounded-3 border bg-body mb-1 p-2"
                  onClick={() =>
                    setSelectedDoctor(
                      selectedDoctor === doctor.id ? null : doctor.id
                    )
                  }
                  style={{ cursor: 'pointer' }}
                >
                  <Row>
                    <Col xs={2} className="text-center">
                      <FontAwesomeIcon
                        icon={faUserMd}
                        size="xl"
                        className={
                          selectedDoctor === doctor.id
                            ? 'text-primary'
                            : 'text-body-quaternary'
                        }
                      />
                    </Col>
                    <Col xs={10}>
                      <h6 className="mb-0">
                        Dr. {doctor.firstName.es} {doctor.lastName.es}
                      </h6>
                      <p className="mb-0 lh-sm fs-10">
                        <strong>Specialty:</strong>{' '}
                        {getSpecialtyName(doctor.specialtyId)}
                        <br />
                        <strong>Locations:</strong>{' '}
                        {doctor.locationIds
                          .map(id => getLocationName(id))
                          .join(', ')}
                      </p>

                      {selectedDoctor === doctor.id && (
                        <div className="mt-0">
                          <Badge bg="success">Selected</Badge>
                        </div>
                      )}
                    </Col>
                  </Row>
                </div>
              ))}
            </div>
          </Tab.Pane>

          {/* SPECIALTIES TAB */}
          <Tab.Pane eventKey="specialties">
            <div className="mb-1">
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search specialties..."
                  value={specialtyFilter}
                  onChange={e => setSpecialtyFilter(e.target.value)}
                />
                {specialtyFilter && (
                  <Button
                    variant="secondary"
                    className="bg-body-quaternary"
                    onClick={() => setSpecialtyFilter('')}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </Button>
                )}
              </InputGroup>
            </div>

            <div
              style={{ height: '250px', overflowY: 'auto' }}
              className="rounded border bg-body-emphasis mb-1 p-2 scrollbar"
            >
              {filteredSpecialties.map(specialty => (
                <div
                  key={specialty.id}
                  className="rounded-3 border bg-body mb-1 p-2"
                  onClick={() =>
                    setSelectedSpecialty(
                      selectedSpecialty === specialty.id ? null : specialty.id
                    )
                  }
                  style={{ cursor: 'pointer' }}
                >
                  <Row>
                    <Col xs={2} className="text-center">
                      <FontAwesomeIcon
                        icon={faUserNurse}
                        size="xl"
                        className={
                          selectedSpecialty === specialty.id
                            ? 'text-primary'
                            : 'text-body-quaternary'
                        }
                      />
                    </Col>
                    <Col xs={10}>
                      <h6 className="mb-0">{specialty.name.es}</h6>
                      <div className="lh-1 fs-10">{specialty.name.en}</div>

                      {/* Display count of doctors with this specialty */}
                      <Badge className="bg-body-quaternary">
                        {
                          doctors.filter(
                            doc => doc.specialtyId === specialty.id
                          ).length
                        }{' '}
                        doctors
                      </Badge>

                      {selectedSpecialty === specialty.id && (
                        <div className="mt-0">
                          <Badge bg="success">Selected</Badge>
                        </div>
                      )}
                    </Col>
                  </Row>
                </div>
              ))}
            </div>
          </Tab.Pane>
        </Tab.Content>

        <h6 className="mb-2">
          Available Time Slots
          <Badge className="ms-2 bg-body-quaternary">
            {filteredSlots.length}
          </Badge>
        </h6>
        {/* Filter by date for slots - shows on all tabs */}
        {availableSlots.length > 0 && (
          <div className="mb-1">
            <InputGroup>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faCalendarCheck} className="me-1" />
                Date Filter
              </InputGroup.Text>
              <Form.Control
                type="date"
                value={dateFilter}
                onChange={e => setDateFilter(e.target.value)}
                min={
                  availableSlots.length > 0
                    ? availableSlots.reduce(
                        (min, slot) => (slot.date < min ? slot.date : min),
                        availableSlots[0].date
                      )
                    : ''
                }
                max={
                  availableSlots.length > 0
                    ? availableSlots.reduce(
                        (max, slot) => (slot.date > max ? slot.date : max),
                        availableSlots[0].date
                      )
                    : ''
                }
              />
              {dateFilter && (
                <Button
                  variant="secondary"
                  className="bg-body-quaternary"
                  onClick={() => setDateFilter('')}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
              )}
            </InputGroup>
          </div>
        )}

        {/* AVAILABLE SLOTS SECTION - Shows below any tab if a selection is made */}
        {availableSlots.length > 0 && (
          <div
            style={{ height: '200px', overflowY: 'auto' }}
            className="rounded border bg-body-emphasis mb-0 p-3 scrollbar"
          >
            {Object.keys(slotsByDate).length === 0 ? (
              <Alert variant="info">
                No available slots for the selected date.
              </Alert>
            ) : (
              Object.keys(slotsByDate)
                .sort()
                .map(date => (
                  <div key={date}>
                    <div className="fw-black">{formatDate(date)}</div>
                    <div className="p-2">
                      <Table hover responsive>
                        <thead>
                          <tr>
                            <th className="p-2 pt-0 pb-0 fs-10">Time</th>
                            <th className="p-0 pt-0 pb-0 fs-10">Location</th>
                            <th className="p-0 pt-0 pb-0 fs-10">Service</th>
                            <th className="p-0 pt-0 pb-0 fs-10">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {slotsByDate[date]
                            .sort((a, b) =>
                              a.startTime.localeCompare(b.startTime)
                            )
                            .map(slot => (
                              <tr
                                key={slot.id}
                                className={slot.booked ? 'table-secondary' : ''}
                              >
                                <td className="p-2 pt-0 pb-0 fs-10">
                                  {slot.startTime}
                                </td>
                                <td className="p-0 pt-0 pb-0 fs-10">
                                  {getLocationName(slot.locationId)}
                                </td>
                                <td className="p-0 pt-0 pb-0 fs-10">
                                  {slot.serviceType === 'doctor'
                                    ? `Dr. ${getDoctorName(slot.serviceId)}`
                                    : getExaminationName(slot.serviceId)}
                                </td>
                                <td className="p-0 pe-1 pt-0 pb-0 fs-10">
                                  {slot.booked ? (
                                    <Badge bg="warning">
                                      <FontAwesomeIcon
                                        icon={faTimes}
                                        className="me-1"
                                      />
                                      Booked
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-body-quaternary">
                                      <FontAwesomeIcon
                                        icon={faCheck}
                                        className="me-1"
                                      />
                                      Available
                                    </Badge>
                                  )}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                ))
            )}
          </div>
        )}
      </Tab.Container>
    </div>
  );
};

export default HVAHealthCareInfoPanel;
